import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, LogOut } from "lucide-react";
import { Logo } from "@/components/Logo";
import api, { formatApiError } from "@/lib/api";

const EMPTY_PRODUCT = { name: "", code: "", category: "Research Peptide", purity: ">99%", size: "5mg", price: 0, description: "", sequence: "", image: "vial", in_stock: true, featured: 0 };

const inputCls =
  "w-full bg-[#FFFFFF] border border-[#E4E4E7] px-3 py-2.5 text-sm text-[#09090B] placeholder:text-[#A1A1AA] focus:outline-none focus:border-[#6D28D9] transition-colors";

const ORDER_STATUSES = ["awaiting_payment", "paid", "shipped", "cancelled"];

const statusCls = (s) =>
  s === "paid" || s === "shipped"
    ? "border-[#6D28D9]/50 text-[#6D28D9]"
    : s === "cancelled"
      ? "border-red-500/40 text-red-400"
      : "border-[#D4D4D8] text-[#71717A]";

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("analytics");
  const [products, setProducts] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const [p, e, o, a] = await Promise.all([
      api.get("/products"), api.get("/enquiries"), api.get("/orders"), api.get("/admin/analytics"),
    ]);
    setProducts(p.data);
    setEnquiries(e.data);
    setOrders(o.data);
    setAnalytics(a.data);
  }, []);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => {
        if (r.data?.role !== "admin") {
          navigate("/admin/login");
          return;
        }
        setUser(r.data);
        load();
      })
      .catch(() => navigate("/admin/login"));
  }, [navigate, load]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...editing, price: Number(editing.price), featured: Number(editing.featured) };
      if (editing.id) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success(editing.id ? "Product updated" : "Product added");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const removeProduct = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    await api.delete(`/products/${p.id}`).catch((err) => toast.error(formatApiError(err)));
    toast.success("Product deleted");
    load();
  };

  const markRead = async (enq) => {
    await api.patch(`/enquiries/${enq.id}`, { status: "read" }).catch(() => {});
    load();
  };

  const setOrderStatus = async (o, status) => {
    await api.patch(`/orders/${o.id}`, { status }).catch((err) => toast.error(formatApiError(err)));
    load();
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FBFBFC] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#6D28D9]" />
      </div>
    );
  }

  const set = (k) => (e) => setEditing((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const newEnquiries = enquiries.filter((e) => e.status === "new").length;
  const pendingOrders = orders.filter((o) => o.status === "awaiting_payment").length;

  const tabs = [
    { key: "analytics", label: "Analytics" },
    { key: "orders", label: `Orders${pendingOrders ? ` (${pendingOrders})` : ""}` },
    { key: "products", label: `Products (${products.length})` },
    { key: "enquiries", label: `Enquiries${newEnquiries ? ` (${newEnquiries})` : ""}` },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#FBFBFC] text-[#09090B]">
      <header className="border-b border-[#E4E4E7] bg-[#FFFFFF]">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo className="w-6 h-6" />
            <span className="font-display font-bold tracking-[0.15em] text-xs uppercase">Aetheris Admin — Private</span>
          </div>
          <div className="flex items-center gap-5">
            <span data-testid="admin-user-email" className="font-mono-clinical text-[11px] text-[#A1A1AA]">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 font-mono-clinical text-[11px] tracking-[0.15em] uppercase text-[#71717A] hover:text-[#6D28D9] transition-colors">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="flex items-center gap-6 mb-8 border-b border-[#E4E4E7]">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`pb-3 font-mono-clinical text-xs tracking-[0.2em] uppercase border-b-2 -mb-px transition-colors ${
                tab === t.key ? "border-[#6D28D9] text-[#6D28D9]" : "border-transparent text-[#A1A1AA] hover:text-[#09090B]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "analytics" && analytics && (
          <div data-testid="admin-analytics-panel" className="space-y-8">
            <h1 className="font-display text-xl font-bold uppercase tracking-tight">Analytics</h1>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#E4E4E7] border border-[#E4E4E7]">
              {[
                { label: "Revenue (paid + shipped)", value: `$${analytics.revenue.toFixed(2)}`, tid: "analytics-revenue" },
                { label: "Orders", value: analytics.total_orders, tid: "analytics-orders" },
                { label: "Avg order value", value: `$${analytics.avg_order_value.toFixed(2)}`, tid: "analytics-aov" },
                { label: "Customers", value: analytics.customers, tid: "analytics-customers" },
                { label: "$15 discounts used", value: analytics.discount_orders, tid: "analytics-discounts" },
              ].map((c) => (
                <div key={c.tid} className="bg-[#FFFFFF] p-5">
                  <p className="font-mono-clinical text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] mb-2">{c.label}</p>
                  <p data-testid={c.tid} className="font-display text-2xl font-bold text-[#09090B]">{c.value}</p>
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-px bg-[#E4E4E7] border border-[#E4E4E7]">
              <div className="bg-[#FFFFFF] p-5">
                <p className="font-mono-clinical text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] mb-3">Orders by status</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(analytics.by_status).map(([s, n]) => (
                    <span key={s} data-testid={`analytics-status-${s}`} className={`font-mono-clinical text-[11px] tracking-[0.1em] uppercase px-3 py-1.5 border ${statusCls(s)}`}>
                      {s.replace("_", " ")} — {n}
                    </span>
                  ))}
                </div>
              </div>
              <div className="bg-[#FFFFFF] p-5">
                <p className="font-mono-clinical text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] mb-3">Payment methods</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(analytics.by_method).map(([m, n]) => (
                    <span key={m} data-testid={`analytics-method-${m}`} className="font-mono-clinical text-[11px] tracking-[0.1em] uppercase px-3 py-1.5 border border-[#E4E4E7] text-[#52525B]">
                      {m} — {n}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-[#FFFFFF] border border-[#E4E4E7] p-5">
              <p className="font-mono-clinical text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] mb-4">Last 14 days — orders (hover for revenue)</p>
              <div data-testid="analytics-chart" className="flex items-end gap-1.5 h-32">
                {analytics.by_day.map((d) => (
                  <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group" title={`${d.date}: ${d.orders} orders, $${d.revenue.toFixed(2)}`}>
                    <div
                      data-testid={`analytics-bar-${d.date}`}
                      className="w-full bg-[#6D28D9]/20 group-hover:bg-[#6D28D9]/50 transition-colors relative"
                      style={{ height: `${Math.max(4, (d.orders / Math.max(1, ...analytics.by_day.map((x) => x.orders))) * 100)}%` }}
                    >
                      {d.orders > 0 && (
                        <span className="absolute -top-5 left-1/2 -translate-x-1/2 font-mono-clinical text-[9px] text-[#6D28D9]">{d.orders}</span>
                      )}
                    </div>
                    <span className="font-mono-clinical text-[8px] text-[#A1A1AA] -rotate-45 origin-top-left whitespace-nowrap">{d.date.slice(5)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#FFFFFF] border border-[#E4E4E7] p-5">
              <p className="font-mono-clinical text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] mb-3">Top products by revenue</p>
              {analytics.top_products.length === 0 ? (
                <p className="text-[#A1A1AA] text-sm font-mono-clinical">No sales yet.</p>
              ) : (
                <div data-testid="analytics-top-products" className="space-y-px bg-[#E4E4E7]">
                  {analytics.top_products.map((t, i) => (
                    <div key={t.name} className="bg-[#FFFFFF] px-4 py-3 flex items-center gap-4">
                      <span className="font-mono-clinical text-[10px] text-[#A1A1AA] w-5">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-sm font-bold text-[#09090B] flex-1">{t.name}</span>
                      <span className="font-mono-clinical text-xs text-[#71717A]">{t.qty} sold</span>
                      <span className="font-mono-clinical text-sm text-[#6D28D9] font-bold w-24 text-right">${t.revenue.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div data-testid="admin-orders-panel">
            <h1 className="font-display text-xl font-bold uppercase tracking-tight mb-2">Orders</h1>
            <p className="text-[#A1A1AA] text-sm mb-6">Only you can see this page — it sits behind your admin login and there are no customer accounts.</p>
            {orders.length === 0 ? (
              <p className="text-[#A1A1AA] text-sm font-mono-clinical" data-testid="admin-orders-empty">No orders yet.</p>
            ) : (
              <div className="space-y-px bg-[#E4E4E7] border border-[#E4E4E7]">
                {orders.map((o) => (
                  <div key={o.id} data-testid={`admin-order-${o.id.slice(-8)}`} className="bg-[#FFFFFF] p-5">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <span className="font-mono-clinical text-xs text-[#09090B]">№ {o.id.slice(-8).toUpperCase()}</span>
                      <span className="font-mono-clinical text-[10px] text-[#A1A1AA]">{o.created_at.slice(0, 16).replace("T", " ")} UTC</span>
                      <span className={`font-mono-clinical text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 border ${statusCls(o.status)}`}>{o.status.replace("_", " ")}</span>
                      <span className="ml-auto text-right">
                        <span className="font-mono-clinical text-sm text-[#6D28D9] font-bold block">${o.total_usd.toFixed(2)}</span>
                        <span className="font-mono-clinical text-[10px] text-[#A1A1AA]">{o.shipping_usd > 0 ? `incl. $${o.shipping_usd.toFixed(0)} shipping` : "free shipping"}</span>
                      </span>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="font-mono-clinical text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] mb-1">Customer</p>
                        <p className="font-bold">{o.name}</p>
                        <p className="text-[#6D28D9] text-xs font-mono-clinical">{o.email}</p>
                        <p className="text-[#71717A] text-xs mt-1 whitespace-pre-line">{o.shipping_address}</p>
                        {o.note && <p className="text-[#A1A1AA] text-xs mt-1">Note: {o.note}</p>}
                      </div>
                      <div>
                        <p className="font-mono-clinical text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] mb-1">Items</p>
                        {o.items.map((i) => (
                          <p key={i.product_id} className="font-mono-clinical text-xs text-[#52525B]">
                            {i.code} — {i.name} {i.size} × {i.qty} <span className="text-[#A1A1AA]">(${i.price * i.qty})</span>
                          </p>
                        ))}
                      </div>
                      <div className="flex md:flex-col gap-2 md:items-end">
                        {ORDER_STATUSES.filter((s) => s !== o.status).map((s) => (
                          <button
                            key={s}
                            onClick={() => setOrderStatus(o, s)}
                            data-testid={`order-${o.id.slice(-8)}-mark-${s}`}
                            className="px-4 py-2 border border-[#E4E4E7] font-mono-clinical text-[10px] tracking-[0.15em] uppercase text-[#71717A] hover:border-[#6D28D9] hover:text-[#6D28D9] transition-colors"
                          >
                            Mark {s.replace("_", " ")}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "products" && (
          <div data-testid="admin-products-panel">
            <div className="flex justify-between items-center mb-6">
              <h1 className="font-display text-xl font-bold uppercase tracking-tight">Products ({products.length})</h1>
              <button
                onClick={() => setEditing({ ...EMPTY_PRODUCT })}
                data-testid="admin-add-product-button"
                className="flex items-center gap-2 px-5 py-2.5 bg-[#6D28D9] text-white font-mono-clinical text-[11px] tracking-[0.15em] uppercase font-bold hover:bg-[#5B21B6] transition-colors"
              >
                <Plus className="w-4 h-4" /> Add Product
              </button>
            </div>
            <div className="border border-[#E4E4E7] overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E4E4E7] bg-[#FFFFFF] font-mono-clinical text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA]">
                    <th className="text-left px-4 py-3">Code</th>
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Size</th>
                    <th className="text-left px-4 py-3">Price</th>
                    <th className="text-left px-4 py-3">Stock</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} data-testid={`admin-product-row-${p.code}`} className="border-b border-[#E4E4E7] hover:bg-[#F4F4F5]">
                      <td className="px-4 py-3 font-mono-clinical text-[#6D28D9] text-xs">{p.code}</td>
                      <td className="px-4 py-3 font-display font-bold uppercase">{p.name}</td>
                      <td className="px-4 py-3 text-[#71717A]">{p.size}</td>
                      <td className="px-4 py-3 text-[#71717A]">${Number(p.price).toFixed(2)}</td>
                      <td className="px-4 py-3">{p.in_stock ? <span className="text-[#6D28D9] text-xs font-mono-clinical">IN STOCK</span> : <span className="text-[#A1A1AA] text-xs font-mono-clinical">ON REQUEST</span>}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => setEditing({ ...p })} data-testid={`admin-edit-${p.code}`} className="p-2 text-[#71717A] hover:text-[#6D28D9] transition-colors"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => removeProduct(p)} data-testid={`admin-delete-${p.code}`} className="p-2 text-[#71717A] hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "enquiries" && (
          <div data-testid="admin-enquiries-panel">
            <h1 className="font-display text-xl font-bold uppercase tracking-tight mb-6">Enquiries ({enquiries.length})</h1>
            {enquiries.length === 0 ? (
              <p className="text-[#A1A1AA] text-sm font-mono-clinical" data-testid="admin-enquiries-empty">No enquiries yet.</p>
            ) : (
              <div className="space-y-px bg-[#E4E4E7] border border-[#E4E4E7]">
                {enquiries.map((enq) => (
                  <div key={enq.id} data-testid={`admin-enquiry-${enq.id}`} className="bg-[#FFFFFF] p-5 flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-display font-bold">{enq.name}</span>
                        <span className="font-mono-clinical text-xs text-[#6D28D9]">{enq.email}</span>
                        {enq.organization && <span className="font-mono-clinical text-xs text-[#A1A1AA]">{enq.organization}</span>}
                        <span className={`font-mono-clinical text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 border ${enq.status === "new" ? "border-[#6D28D9]/50 text-[#6D28D9]" : "border-[#E4E4E7] text-[#A1A1AA]"}`}>{enq.status}</span>
                      </div>
                      {enq.product_interest && <p className="text-xs text-[#71717A] mt-1 font-mono-clinical">Interest: {enq.product_interest}</p>}
                      <p className="text-sm text-[#71717A] mt-2 leading-relaxed">{enq.message}</p>
                    </div>
                    {enq.status === "new" && (
                      <button onClick={() => markRead(enq)} data-testid={`admin-mark-read-${enq.id}`} className="shrink-0 px-4 py-2 border border-[#E4E4E7] font-mono-clinical text-[10px] tracking-[0.15em] uppercase text-[#71717A] hover:border-[#6D28D9] hover:text-[#6D28D9] transition-colors">
                        Mark Read
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-6" data-testid="admin-product-modal">
          <form onSubmit={saveProduct} className="w-full max-w-lg bg-[#FFFFFF] border border-[#E4E4E7] p-8 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="font-display text-lg font-bold uppercase tracking-tight mb-2">{editing.id ? "Edit Product" : "New Product"}</h2>
            <div className="grid grid-cols-2 gap-4">
              <input required placeholder="Name (e.g. BPC-157)" data-testid="product-form-name" value={editing.name} onChange={set("name")} className={inputCls} />
              <input required placeholder="Code (e.g. AX-0157)" data-testid="product-form-code" value={editing.code} onChange={set("code")} className={inputCls} />
              <input placeholder="Category" value={editing.category} onChange={set("category")} className={inputCls} />
              <input placeholder="Purity" value={editing.purity} onChange={set("purity")} className={inputCls} />
              <input placeholder="Size" value={editing.size} onChange={set("size")} className={inputCls} />
              <input required type="number" step="0.01" placeholder="Price (USD)" data-testid="product-form-price" value={editing.price} onChange={set("price")} className={inputCls} />
            </div>
            <textarea rows={3} placeholder="Description" value={editing.description} onChange={set("description")} className={`${inputCls} resize-none`} />
            <input placeholder="Sequence (optional)" value={editing.sequence} onChange={set("sequence")} className={inputCls} />
            <label className="flex items-center gap-3 text-sm text-[#71717A] font-mono-clinical text-xs uppercase tracking-[0.15em]">
              <input type="checkbox" checked={editing.in_stock} onChange={set("in_stock")} className="accent-[#6D28D9] w-4 h-4" />
              In stock
            </label>
            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={saving} data-testid="product-form-save" className="flex-1 px-6 py-3 bg-[#6D28D9] text-white font-mono-clinical text-xs tracking-[0.15em] uppercase font-bold hover:bg-[#5B21B6] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
              </button>
              <button type="button" onClick={() => setEditing(null)} data-testid="product-form-cancel" className="px-6 py-3 border border-[#E4E4E7] font-mono-clinical text-xs tracking-[0.15em] uppercase text-[#71717A] hover:text-[#09090B] transition-colors">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
